import { cmsNew, useCmsNew, type CmsNewKey } from "@/lib/cms/cms-new-i18n";
import { Label } from "@/components/ui/label";
import { type BlockCornerRadius } from "@/lib/cms-puck/block-style";
import { CMS_PALETTE_TEXT_INPUT_CLASS } from "@/components/cms/theme/cms-color-input-styles";
import { cn } from "@/lib/utils";
import { StyleSection } from "./StyleSection";
import { ControlRow, IconToggle } from "./controls";

const RADIUS_LABELS: Array<[keyof BlockCornerRadius, CmsNewKey, string]> =
  [
    ["tl", "cms.new.auto.option.tl.haut_g", "Haut G."],
    ["tr", "cms.new.auto.option.tr.haut_d", "Haut D."],
    ["bl", "cms.new.auto.option.bl.bas_g", "Bas G."],
    ["br", "cms.new.auto.option.br.bas_d", "Bas D."],
  ];

function RadiusCornerInput({
  corner,
  label,
  value,
  onChange,
}: {
  corner: keyof BlockCornerRadius;
  label: string;
  value: number | null;
  onChange: (corner: keyof BlockCornerRadius, value: number | null) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium text-slate-500">{label}</Label>
      <input
        type="number"
        min={0}
        value={value != null ? value : ""}
        placeholder={cmsNew("cms.new.auto.placeholder.0", "0")}
        aria-label={`${cmsNew("cms.new.fields.style.radiusAria", "Rayon")} ${label}`}
        className={cn(
          CMS_PALETTE_TEXT_INPUT_CLASS,
          "h-9 w-full text-center tabular-nums",
        )}
        onChange={(e) => {
          const raw = e.target.value;
          const n = raw === "" ? null : Number(raw);
          onChange(
            corner,
            n != null && Number.isFinite(n) && n >= 0 ? n : null,
          );
        }}
      />
    </div>
  );
}

/** Coins arrondis (border-radius) — section dédiée, tous les blocs. */
export function RadiusControls({
  radius,
  onRadiusChange,
}: {
  radius: BlockCornerRadius;
  onRadiusChange: (radius: BlockCornerRadius) => void;
}) {
  const cmsNew = useCmsNew();
  const uniform =
    radius.tl != null &&
    radius.tl === radius.tr &&
    radius.tl === radius.br &&
    radius.tl === radius.bl
      ? radius.tl
      : null;

  const setUniform = (n: number | null) => {
    onRadiusChange({ tl: n, tr: n, br: n, bl: n });
  };

  const RADIUS_PRESETS: Array<[number, string]> = [
    [0, "0"],
    [8, "8"],
    [16, "16"],
    [24, "24"],
    [9999, "Max"],
  ];

  return (
    <StyleSection
      title={cmsNew("cms.new.auto.title.coins_arrondis", "Coins arrondis")}
      description={cmsNew("cms.new.auto.description.rayon_uniforme_ou_par_coin_px_sapplique_au_conteneur_du_bloc", "Rayon uniforme ou par coin (px). S'applique au conteneur du bloc.")}
    >
      <div className="space-y-4">
        <ControlRow label={cmsNew("cms.new.auto.label.rayon_uniforme", "Rayon uniforme")}>
          {RADIUS_PRESETS.map(([n, short]) => (
            <IconToggle
              key={short}
              label={
                n === 9999
                  ? cmsNew(
                      "cms.new.fields.style.radiusMaxLabel",
                      "Max — coins entièrement arrondis",
                    )
                  : `${short} px`
              }
              active={uniform === n}
              onClick={() => setUniform(n)}
            >
              <span className="text-[10px] font-semibold tabular-nums">
                {short}
              </span>
            </IconToggle>
          ))}
          <input
            type="number"
            min={0}
            value={uniform != null ? uniform : ""}
            placeholder={cmsNew("cms.new.auto.placeholder.px", "px")}
            title={cmsNew("cms.new.auto.title.rayon_personnalise_px", "Rayon personnalisé (px)")}
            className={cn(
              CMS_PALETTE_TEXT_INPUT_CLASS,
              "h-9 w-20 text-center tabular-nums",
            )}
            onChange={(e) => {
              const raw = e.target.value;
              const n = raw === "" ? null : Number(raw);
              setUniform(n != null && Number.isFinite(n) && n >= 0 ? n : null);
            }}
          />
          <span className="text-[10px] text-slate-400">{cmsNew("cms.new.auto.text.px", "px")}</span>
        </ControlRow>

        {/* 2 colonnes (et non 4) : chaque input reste assez large pour lire la
            valeur, même quand la colonne de droite n'est pas élargie. */}
        <div className="grid grid-cols-2 gap-3">
          {RADIUS_LABELS.map(([corner, key, fallback]) => (
            <RadiusCornerInput
              key={corner}
              corner={corner}
              label={cmsNew(key, fallback)}
              value={radius[corner]}
              onChange={(c, n) => onRadiusChange({ ...radius, [c]: n })}
            />
          ))}
        </div>
      </div>
    </StyleSection>
  );
}
